import { Injectable } from '@angular/core';
import type { Locales } from '../../gensrc/i18n/i18n-types';
import { DatabaseWrapper, DatabaseWrapperProvider } from 'services/database-wrapper';
import { AuthService } from 'services/auth.service';
import { ENGLISH_TEXT_SEGMENT, textSegmentForLocale } from 'services/text-locale';
import { generateAsyncInitShutdownPair } from '@cf-shared/shared-util';
import { openCommonDbAsync } from 'services/common-db-open';


@Injectable()
export class CommonDbService {
  database: DatabaseWrapper;

  constructor(
    private auth: AuthService,
    private dbProvider: DatabaseWrapperProvider,
  ) {
    let { init, shutdown } = generateAsyncInitShutdownPair(async () => this.initializeAsync_internal(), async () => this.shutdownAsync_internal());
    this.initializeAsync = init;
    this.shutdownAsync = shutdown;
  }

  initializeAsync: () => Promise<void>;
  shutdownAsync: () => Promise<void>;

  /**
   * Warm resume: open local PouchDB only, no network.
   *
   * df-54ca73a44654: the wrapper is handed out only once its open succeeded — publishing first left
   * a half-built one on the service, looking ready to everything that reads the field. A failed open
   * is retired rather than dropped, because the provider registered the wrapper the moment it was
   * made. Same ordering, and same reason, as `openCommonDbAsync` uses for the cold path.
   */
  async initializeLocalOnlyAsync() {
    const database = this.dbProvider.createDatabaseWrapper();
    try {
      await database.initAsSharedDatabaseLocalOnlyAsync(this.auth.user, 'global_common');
    } catch (error) {
      this.retireWrapperAfterFailedOpen(database);
      throw error;
    }
    this.database = database;
  }

  // warm resume Phase B: connect remote and start sync
  async connectRemoteAsync() {
    await this.database?.connectRemoteAndSyncAsync();
  }

  async initializeAsync_internal() {
    // the ordering — publish only once the open succeeded — lives in `openCommonDbAsync`, which is
    // node-loadable and unit-tested. This service supplies the real work and the side effects.
    await openCommonDbAsync<DatabaseWrapper>({
      existingDatabase: this.database,
      // a wrapper that has been shut down is not something to resume onto — `isShuttingDown` is
      // true from the instant a shutdown starts, and is already what the rest of the code means
      // by a dead wrapper (see `selectActiveDatabases` and `mayCloseLocalStore`)
      isDatabaseUsable: database => !database.isShuttingDown,
      connectRemoteAsync: database => database.connectRemoteAndSyncAsync(),
      createDatabase: () => this.dbProvider.createDatabaseWrapper(),
      openDatabaseAsync: database => database.initAsSharedDatabaseAsync(this.auth.user, 'global_common'),
      retireFailedDatabase: database => this.retireWrapperAfterFailedOpen(database),
      publishDatabase: database => { this.database = database; },
    });
  }

  /**
   * Retire a wrapper whose open failed, so it stops being swept as a live database.
   *
   * `createDatabaseWrapper` pushes every wrapper it makes into the provider's `allDatabases` for
   * good, and only a STARTED shutdown takes one out of `activeDatabases`. So a discarded wrapper
   * has to be shut down rather than merely dropped.
   *
   * Deliberately not awaited: `shutdownAsync` waits for a write lock, and letting that wait sit
   * in front of the original failure would delay — or with a wedged lock, never deliver — the
   * error the caller needs. A failure of the cleanup itself is reported rather than swallowed,
   * and never replaces the error the caller sees.
   */
  private retireWrapperAfterFailedOpen(database: DatabaseWrapper) {
    void database.shutdownAsync().catch((error) => {
      this.dbProvider.logging.trackSilentProblem('CommonDbFailedOpenCleanupFailed', {
        message: (error as Error)?.message,
      });
    });
  }

  async shutdownAsync_internal() {
    if (this.database) {
      await this.database.shutdownAsync();
      // df-54ca73a44654: deliberately NOT nulled, for the same reason `UserDbService` gives — a
      // great many callers read `commonDb.database` without a null check, and in-flight ones would
      // crash on `null` where the shut-down wrapper safely does nothing. What the next open must
      // not do is RESUME onto this dead wrapper, and that is handled where the decision is made:
      // `isDatabaseUsable` in `initializeAsync_internal`, which rebuilds instead.
    }
  }

  /**
   * Fetch a language's curated `$text/` documents BEFORE the app switches to it
   * and reloads (#4284). Because `global_common` now syncs only English plus the
   * active language, the newly chosen language's documents are not on the device
   * yet at switch time; pulling them first means the app comes back already
   * translated instead of silently half-English.
   *
   * Returns whether that language is now on the device. English needs no pull (it
   * is always present), so it short-circuits to `true`. A `false` result — offline
   * or a failed/timed-out pull — tells the caller to keep the current language and
   * warn the user rather than switch into a partly translated app.
   */
  async syncLanguageTextBeforeSwitchAsync(newLocale: Locales): Promise<boolean> {
    const segment = textSegmentForLocale(newLocale);
    // English is always resident (it is the fallback), so there is nothing to pull
    if (segment === ENGLISH_TEXT_SEGMENT) return true;
    if (!this.database) return false;
    return this.database.pullLocaleTextAsync(segment);
  }

}
